import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../ui/components/ui/select';
import { FilterMetaYearResponseDto, MetricDetailsResponseDto, SectionMetaResponseDto } from '../../types/response';
import KpiMetricGraph from '../kpi-metric-graph/kpi-metric-graph';
import KpiWeekRangeSelectorComponent from '../kpi-week-selector/kpi-week-range-selector';

interface WeekRange {
  fromYear: number;
  fromWeek: number;
  toYear: number;
  toWeek: number;
}

export interface KpiDetailCardComponentProps {
  years: FilterMetaYearResponseDto[];
  sections: SectionMetaResponseDto[];
  selectedSectionKey: string;
  selectedCardKey: string;
  selectedMetricKey: string;
  range: WeekRange;
  details: MetricDetailsResponseDto | null;
  isLoading: boolean;
  onSectionChange: (sectionKey: string) => void;
  onCardChange: (cardKey: string) => void;
  onMetricChange: (metricKey: string) => void;
  onRangeChange: (range: { from: { week: string; year: string }; to: { week: string; year: string } }) => void;
  className?: string;
}

const GraphSkeleton = () => <div className="h-[100px] w-full bg-[#E4E7EC] rounded-xl animate-pulse" />;

const KpiDetailCardComponent = ({
  years,
  sections,
  selectedSectionKey,
  selectedCardKey,
  selectedMetricKey,
  range,
  details,
  isLoading,
  onSectionChange,
  onCardChange,
  onMetricChange,
  onRangeChange,
  className
}: KpiDetailCardComponentProps) => {
  const selectedSection = sections.find((s) => s.sectionKey === selectedSectionKey);
  const cards = selectedSection?.cards ?? [];
  const selectedCard = cards.find((c) => c.cardKey === selectedCardKey);
  const metrics = selectedCard?.values ?? [];

  return (
    <div className={`bg-white rounded-2xl border border-[#E4E7EC] p-5 ${className}`} style={{ boxShadow: '0px 4px 12.7px 0px rgba(0, 0, 0, 0.02)' }}>
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <span className="text-lg font-bold text-[#12282A]">Detailansicht</span>

        <div className="flex items-center gap-5 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Section */}
            <Select value={selectedSectionKey} onValueChange={onSectionChange}>
              <SelectTrigger className="h-8 text-xs font-medium w-[130px] rounded-md border border-input">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sections.map((s) => (
                  <SelectItem key={s.sectionKey} value={s.sectionKey} className="text-xs">
                    {s.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Card */}
            <Select value={selectedCardKey} onValueChange={onCardChange}>
              <SelectTrigger className="h-8 text-xs font-medium w-[130px] rounded-md border border-input">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {cards.map((c) => (
                  <SelectItem key={c.cardKey} value={c.cardKey} className="text-xs">
                    {c.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Metric */}
            <Select value={selectedMetricKey} onValueChange={onMetricChange}>
              <SelectTrigger className="h-8 text-xs font-medium w-[130px] rounded-md border border-input">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {metrics.map((m) => (
                  <SelectItem key={m.metricKey} value={m.metricKey} className="text-xs">
                    {m.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Range */}
          <KpiWeekRangeSelectorComponent
            years={years}
            currentFromYear={range.fromYear}
            currentFromWeek={range.fromWeek}
            currentToYear={range.toYear}
            currentToWeek={range.toWeek}
            onChange={onRangeChange}
          />
        </div>
      </div>

      {/* Graph */}
      {isLoading ? <GraphSkeleton /> : <KpiMetricGraph items={details?.items ?? []} className="w-full" />}
    </div>
  );
};

export default KpiDetailCardComponent;
